import { Show, onMount, createSignal, createMemo, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { getChatbotLoader } from '../../tools/chatbots/chatbot-loader';
import { formatOpeningHours, isChatOpen } from './opening-hours';
import { SmallCornerDialog } from '../small-corner-dialog/small-corner-dialog';
import { VerticalSpace } from '../../ui-components/utility-style-components/spacing';
import { Button } from '../../ui-components/button/button';
import { Text } from '../../ui-components/text/text';
import chatIcon from '../../../assets/CP-Icon-Bubble-With-Dots-White-RGB.png';
const chatbotSupportedSites = ['de-ch', 'fr-ch', 'fr', 'en-us', 'en-au'];

export const Chatbot = () => {
    const {
        siteInfo,
        siteDomains,
        deploymentEnvironment,
        localize,
        chatSettings,
    } = useContext(AppContext);

    const [chatbotLoaded, setChatbotLoaded] = createSignal(false);
    const [consentGivenState, setConsentGivenState] = createSignal(false);
    const [showChatbotCookieConsentDialog, setShowChatbotCookieConsentDialog] = createSignal(false);
    const [showChatClosedDialog, setShowChatClosedDialog] = createSignal(false);

    // Check if the site supports chatbot (hardcoded list + CMS setting)
    const isChatbotEnabledSite = createMemo(() => {
        const isInSupportedSites = chatbotSupportedSites.includes(siteInfo.siteLanguage || '') && siteInfo.siteType !== 'atos-care';
        return isInSupportedSites && chatSettings.enabled;
    });

    // Get the chat closed message with opening hours substituted
    const getChatClosedMessage = createMemo(() => {
        const { closedMessage, openingHours } = chatSettings;
        const openingHoursText = formatOpeningHours(openingHours, siteInfo.siteLanguage);

        if (closedMessage) {
            return closedMessage.replace('{openingHours}', openingHoursText);
        }

        if (openingHoursText) {
            return localize('chat-closed-with-hours', `Our chat is currently closed. We are available ${openingHoursText}.`).replace('{openingHours}', openingHoursText);
        }

        return localize('chat-closed-default-message', 'Our chat is currently closed.');
    });

    onMount(() => {
        if (!isChatbotEnabledSite()) {
            return;
        }

        checkConsentForChatbot();

        // Listen for changes in consent
        window.addEventListener('CookiebotOnAccept', () => {
            checkConsentForChatbot();
        });
    });

    // The real chat only loads while the chat is open; outside opening hours the fake button stays
    const loadRealChatbot = () => {
        if (chatbotLoaded() || !isChatOpen(chatSettings)) {
            return;
        }

        const scriptLoader = getChatbotLoader(siteInfo.target, siteInfo.siteId, siteDomains, deploymentEnvironment);

        if (scriptLoader) {
            scriptLoader();
            setChatbotLoaded(true);
        }
    };

    const checkConsentForChatbot = () => {
        if (window?.Cookiebot?.consent?.marketing && window?.Cookiebot?.consent?.statistics) {
            setConsentGivenState(true);
            loadRealChatbot();
        } else {
            // Consent previously given, but now revoked
            if (consentGivenState()) {
                setConsentGivenState(false);
                window.location.reload();
            }
        }
    };

    const handleOnClickCookieConsentToggle = (e: any) => {
        e.preventDefault();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.CookieConsent?.renew();
    };

    const handleFakeChatbotClick = () => {
        // Checked on click, not memoised, so a tab left open across opening or closing time is still correct
        if (!isChatOpen(chatSettings)) {
            setShowChatClosedDialog(true);
        } else if (consentGivenState()) {
            // The chat has opened since the page loaded
            loadRealChatbot();
        } else {
            setShowChatbotCookieConsentDialog(true);
        }
    };

    const FakeChatbotIcon = () => (
        <button
            class="fixed bottom-[2.4rem] right-[2.6rem] w-20 h-20 bg-[#00A2BA] p-[0.3rem] rounded-half flex justify-center items-center shadow-[2px_2px_5px_0_rgba(45,45,45,0.5)] cursor-pointer z-[1000] transition-colors duration-200 ease-in-out hover:bg-[#00788A]"
            onClick={handleFakeChatbotClick}
            aria-label={localize('chat-with-a-representative', 'Chat with a representative')}
        >
            {/* Client-approved white chat icon (CP-Icon-Bubble-With-Dots-White), used as
                delivered, on a turquoise launcher to match the active-chat icon. */}
            <img
                src={chatIcon}
                alt={localize('chat-icon', 'Chat icon')}
                class="w-full h-full object-contain"
            />
        </button>
    );

    const ChatbotCookieWarning = () => (
        <SmallCornerDialog>
            <Text
                fontSize='small'
                displayRedVerticalLine={false}
                align='center'
                fontStyle='bold'
                noBlockSpacing={true}
            >
                {localize('chatbot-teaser-heading', 'Chatbot available')}
            </Text>
            <VerticalSpace size={1} />
            <Text
                fontSize='small'
                displayRedVerticalLine={false}
                align='center'
            >
                {localize('chatbot-teaser-text', 'To use the chatbot you must consent to our marketing and statistics policies')}
            </Text>
            <div class="flex flex-row justify-center items-center gap-4 max-mobile:flex-col max-mobile:gap-0">
                <Button
                    label={localize('chatbot-teaser-open-cookie-consent-button-text', 'Open cookie consent')}
                    onClick={handleOnClickCookieConsentToggle}
                    variant='primary'
                    small={true}
                    customCss='white-space: nowrap;'
                    noBlockSpacing
                />
                <Button
                    label={localize('chatbot-teaser-dismiss-button-text', 'Dismiss')}
                    onClick={() => setShowChatbotCookieConsentDialog(false)}
                    variant='secondary'
                    small={true}
                    customCss='white-space: nowrap;'
                    noBlockSpacing
                />
            </div>
        </SmallCornerDialog>
    );

    const ChatClosedDialog = () => (
        <SmallCornerDialog>
            <div style={{ 'min-width': '280px', 'text-align': 'center' }}>
                <Text
                    fontSize='small'
                    displayRedVerticalLine={false}
                    align='center'
                    fontStyle='bold'
                    noBlockSpacing={true}
                >
                    {chatSettings.closedHeading || localize('chat-closed-heading', 'Chat closed')}
                </Text>
                <VerticalSpace size={1} />
                <Text
                    fontSize='small'
                    displayRedVerticalLine={false}
                    align='center'
                >
                    {getChatClosedMessage()}
                </Text>
                <div class="flex flex-row justify-center items-center gap-4 max-mobile:flex-col max-mobile:gap-0">
                    <Button
                        label={localize('close', 'Close')}
                        onClick={() => setShowChatClosedDialog(false)}
                        variant='primary'
                        small={true}
                    />
                </div>
            </div>
        </SmallCornerDialog>
    );

    return (
        <Show when={!chatbotLoaded()}>
            <Show when={isChatbotEnabledSite()}>
                <FakeChatbotIcon />
            </Show>

            <Show when={showChatbotCookieConsentDialog()}>
                <ChatbotCookieWarning />
            </Show>

            <Show when={showChatClosedDialog()}>
                <ChatClosedDialog />
            </Show>
        </Show>
    );
};
